// frontend/src/lib/authBroadcast.ts — Layer 3 of the freshness ladder.
//
// Single same-browser BroadcastChannel `auth` carrying three message types
// per ADR-003 §Revocation propagation + ADR-016. Same-tab echo is suppressed
// by the BroadcastChannel spec — emitting from one tab delivers only to
// other tabs in the same origin.
//
// Read with: FRONTEND-PATTERNS §State (Layer 3 row), SECURITY §Frontend
// security posture §Cross-tab session sync.
//
// Three messages:
//   logout         — actor's tab cleared cache + redirected; siblings do the same.
//   login          — actor's tab finished login (post-MFA or no-MFA path);
//                    siblings clear + hard-reload so the new user's /me
//                    bootstrap runs cleanly (i18n language, theme brand,
//                    route reset all need cold start when identity changes).
//   me-invalidated — actor's tab ran a self-mutating mutation (theme,
//                    password, future profile fields) and invalidated ['me'];
//                    siblings invalidate too so they don't show stale data
//                    for up to 60s.
//
// Defensive against non-browser hosts: `globalThis.BroadcastChannel`
// resolves to `undefined` in SSR and in some test environments (jsdom 29
// does not expose it on window). Both `broadcastAuth` and `subscribeAuth`
// degrade to silent no-ops in that case — callers do not need to guard.
//
// Listener mounts once at app boot inside `RootLayout`'s `AppSideEffects`.

const CHANNEL_NAME = 'auth';

export type AuthBroadcastMessage =
  | { type: 'logout' }
  | { type: 'login' }
  | { type: 'me-invalidated' };

type AuthBroadcastHandler = (message: AuthBroadcastMessage) => void;

function getBroadcastChannelCtor(): typeof BroadcastChannel | undefined {
  if (typeof globalThis === 'undefined') return undefined;
  const ctor = (globalThis as { BroadcastChannel?: typeof BroadcastChannel })
    .BroadcastChannel;
  return typeof ctor === 'function' ? ctor : undefined;
}

// Lazy singleton — constructed on first emit or subscribe so that an
// unused (or unsupported) environment costs nothing. Module-load-time
// construction would force a stub branch in SSR.
let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (channel) return channel;
  const Ctor = getBroadcastChannelCtor();
  if (!Ctor) return null;
  channel = new Ctor(CHANNEL_NAME);
  return channel;
}

function isAuthBroadcastMessage(value: unknown): value is AuthBroadcastMessage {
  if (typeof value !== 'object' || value === null) return false;
  const t = (value as { type?: unknown }).type;
  return t === 'logout' || t === 'login' || t === 'me-invalidated';
}

export function broadcastAuth(message: AuthBroadcastMessage): void {
  const ch = getChannel();
  if (!ch) return;
  ch.postMessage(message);
}

export function subscribeAuth(handler: AuthBroadcastHandler): () => void {
  const ch = getChannel();
  if (!ch) return () => {};
  const listener = (event: MessageEvent) => {
    if (!isAuthBroadcastMessage(event.data)) return;
    handler(event.data);
  };
  ch.addEventListener('message', listener);
  return () => {
    ch.removeEventListener('message', listener);
  };
}

// Test-only: reset the lazy channel so that `vi.stubGlobal('BroadcastChannel', ...)`
// can swap the constructor between tests. Module-private otherwise.
export function __resetAuthBroadcastForTests(): void {
  if (channel) {
    try {
      channel.close();
    } catch {
      /* ignore */
    }
  }
  channel = null;
}
